import { select } from '@clack/prompts'
import { Command } from 'commander'
import { z } from 'zod'

import { BaseCollections } from '@scalar-org/entities/database'
import { sdkSchema } from '@scalar-org/entities/sdk'
import { teamSchema } from '@scalar-org/entities/team'
import { namespaceSchema, slugSchema } from '@scalar-org/entities/utility'

import { apiService, getAuthData, getDatabaseClient } from '@/helpers'
import { output } from '@/helpers/std-out'

export const UpdateSdkCommand = () => {
  const cmd = new Command('update')

  cmd.description('Update SDK metadata.')

  cmd.option('-s, --slug <slug>', 'SDK slug')
  cmd.option('-n, --namespace <namespace>', 'Team namespace')

  cmd.option('--title <title>', 'Title')
  cmd.option('--isPrivate <isPrivate>', 'Privacy (public/private)')

  cmd.action(async (opts) => {
    const { error, data } = z
      .object({
        slug: slugSchema.optional(),
        namespace: namespaceSchema.optional(),
        title: z.string().max(50).optional(),
        isPrivate: z.boolean().optional(),
      })
      .safeParse(opts)

    if (error)
      return output
        .error()
        .title('Invalid input')
        .message(error.issues[0].message)
        .exit('error')

    const auth = await getAuthData()
    auth.addEventListener((state) => Object.assign(auth, state))

    const db = await getDatabaseClient()

    const client = await apiService()

    const team = await db.getOne({
      collectionPath: BaseCollections.Teams,
      query: {
        uid: auth.teamUid,
      },
      schema: teamSchema,
    })

    if (team.error)
      return output
        .error()
        .title('An error occurred when fetching your team.')
        .message(team.message)
        .exit('error')

    /**
     * Most teams will have 1 namespace, so we default to that.
     * Ideally we should also have a global cli namespace selected.
     */
    const namespace = data.namespace || team.data.namespaces.at(0)

    if (!namespace) {
      return output
        .error()
        .title('Missing namespace')
        .message(
          'Could not find a valid namespace for your SDK. Please specify a namespace or contact support.',
        )
        .exit('error')
    }

    const sdks = await db.getMany({
      collectionPath: BaseCollections.Sdks(team.data.uid),
      query: {
        namespace,
      },
      schema: sdkSchema.array(),
    })

    if (sdks.error) return output.error().title(sdks.message).exit('error')

    const sdkUid =
      sdks.data.find((sdk) => sdk.slug === data.slug)?.uid ||
      ((await select({
        message: 'Please select an SDK to update',
        options: sdks.data.map((sdk) => ({
          value: sdk.uid,
          label: sdk.slug,
        })),
      })) as string)

    if (!sdkUid) {
      return output
        .error()
        .title('No SDK found')
        .message('Please provide a valid SDK slug')
        .exit('error')
    }

    const logger = output.info().loader('Updating the SDK')

    const response = await client.sdks.update(sdkUid, {
      title: data.title,
      isPrivate: data.isPrivate,
    })

    if (response.error) {
      return output
        .error()
        .title('Something went wrong')
        .message(response.message)
        .exit('error')
    }

    logger
      .update('loader', 0, { content: 'SDK updated successfully!' })
      .exit('success')
  })

  return cmd
}
